// src/pages/Scheduler.js
import React, { useState, useEffect, useContext } from "react";
import { request } from "../utils/request";
import { ColorModeContext } from "../index";  // ← make sure this path is correct

const SchedulerComponent = () => {
  const { mode } = useContext(ColorModeContext);
  const textColor = mode === "dark" ? "#ffffff" : "#000000";

  const [enabled, setEnabled] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState("");
  const [time, setTime] = useState("");
  const [frequency, setFrequency] = useState(1);
  const [schedulerId, setSchedulerId] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [message, setMessage] = useState("");

  // dynamic inline styles
  const containerStyle = {
    padding: "20px",
    width: "100%",
    margin: "20px auto",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontFamily: "Arial, sans-serif",
    boxSizing: "border-box",
    color: textColor,            // apply to all text & links
  };

  const toggleButtonStyle = {
    backgroundColor: enabled ? "green" : "red",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginBottom: "20px",
  };

  const formStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    marginBottom: "20px",
  };

  const labelStyle = {
    marginBottom: "5px",
    fontWeight: "bold",
    color: textColor,            // labels follow textColor
  };

  const inputStyle = {
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    width: "100%",
    boxSizing: "border-box",
    color: textColor,            // input text
    backgroundColor: mode === "dark" ? "#2d2d2d" : "#ffffff", // optional contrast
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "20px",
    color: textColor,            // table cell text
  };

  const thStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: mode === "dark" ? "#2d3448" : "#f2f2f2",
    color: mode === "dark" ? "#ffffff" : "#000000",
    textAlign: "left",
  };

  const tdStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    color: textColor,
  };

  useEffect(() => {
    const fetchScheduler = async () => {
      try {
        const data = await request("/scheduler", { method: "GET" });
        if (data.success && data.schedulers.length > 0) {
          const sched = data.schedulers[0];
          setSchedulerId(sched._id);
          setEnabled(sched.enabled);
          setDayOfWeek(sched.dayOfWeek);
          setTime(sched.time);
          setFrequency(sched.frequency);
          setScheduler(sched);
        }
      } catch (error) {
        console.error("Error fetching scheduler:", error);
      }
    };
    fetchScheduler();
  }, []);

  const handleToggle = () => {
    setEnabled((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const payload = { enabled, dayOfWeek, time, frequency };

    try {
      const data = schedulerId
        ? await request(`/scheduler/${schedulerId}`, {
            method: "PUT",
            body: payload,
          })
        : await request("/scheduler", {
            method: "POST",
            body: payload,
          });

      if (data.success) {
        setMessage("Scheduler saved successfully!");
        if (!schedulerId) {
          setSchedulerId(data.scheduler._id);
        }
        setScheduler(data.scheduler);
      } else {
        setMessage("Failed to save scheduler.");
      }
    } catch (error) {
      console.error("Error saving scheduler:", error);
      setMessage("Error saving scheduler.");
    }
  };

  return (
    <div style={containerStyle}>
      <h2 style={{ textAlign: "center", color: textColor }}>
        Notification Scheduler
      </h2>

      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontWeight: "bold", color: textColor }}>
          Enable Notifications:
        </span>
        <button onClick={handleToggle} style={toggleButtonStyle}>
          {enabled ? "Yes" : "No"}
        </button>
      </div>

      {enabled && (
        <div style={formStyle}>
          <div>
            <label htmlFor="dayOfWeek" style={labelStyle}>
              Day of the Week:
            </label>
            <select
              id="dayOfWeek"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              style={inputStyle}
              required
            >
              <option value="">Select a day</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </div>

          <div>
            <label htmlFor="time" style={labelStyle}>
              Time:
            </label>
            <input
              type="time"
              id="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label htmlFor="frequency" style={labelStyle}>
              Frequency:
            </label>
            <select
              id="frequency"
              value={frequency}
              onChange={(e) => setFrequency(parseInt(e.target.value))}
              style={inputStyle}
              required
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <button
          onClick={handleSubmit}
          style={{
            padding: "10px 16px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Submit Changes
        </button>
      </div>

      {enabled && scheduler && (
        <>
          <h3
            style={{ marginTop: "30px", textAlign: "center", color: textColor }}
          >
            Current Scheduler Settings
          </h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Day</th>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Frequency</th>
                <th style={thStyle}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>{scheduler.dayOfWeek}</td>
                <td style={tdStyle}>{scheduler.time}</td>
                <td style={tdStyle}>{scheduler.frequency}</td>
                <td style={tdStyle}>{scheduler.enabled ? "Yes" : "No"}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {message && (
        <p
          style={{
            textAlign: "center",
            marginTop: "20px",
            color: mode === "dark" ? "#ffffff" : "#333333",
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default SchedulerComponent;
